import { z } from 'zod'

/**
 * The typefaces a template may name, and where each one actually works.
 *
 * The distinction is the whole point of this file. An email carries a font only if the RECIPIENT
 * already has it installed — Gmail and Outlook strip `@font-face` and stylesheet links, so a web
 * font in an email is a font nobody sees. A Google Doc is the opposite: the family name is resolved
 * by Google server-side, so anything in its catalogue works and nothing has to be delivered.
 *
 * So a face declares its targets, and the editor only offers what the template's target can keep.
 * Without that, picking DM Sans for an email would preview beautifully and arrive as Times New
 * Roman — the preview lying about the result, which is the one thing this system must not do.
 *
 * None of this costs bundle size. A face is a string here, a CSS stack in an email, and a family
 * name in a Docs request; no font file is ever shipped by us. Only the doc PREVIEW fetches
 * anything, and only for the families a template actually uses.
 */
export interface FontFace {
  id: string
  label: string
  /** The CSS stack an email uses. Ordered: the wanted face, then the closest widely-installed one. */
  stack: string
  /** The single family name a Google Doc wants, or null when the doc has no equivalent. */
  docsFamily: string | null
  /**
   * `email` means widely installed on the machines that read mail — the only fonts an email can
   * rely on. `doc` means Google resolves it.
   */
  targets: readonly ('email' | 'doc')[]
  /** Needs fetching to preview faithfully. System faces do not. */
  webFont?: boolean
}

/**
 * System faces first — these are what the 56 legacy templates use, because they are what is
 * actually installed on the machines reading the mail. Microsoft's faces (Calibri, Aptos, Cambria,
 * Segoe UI) are email-only: Google Docs has no licence for them and would silently substitute.
 */
/** What a template without a master font renders in — the 'System sans' entry below. */
export const DEFAULT_MASTER_FONT = 'system-sans'

export const FONT_FACES: readonly FontFace[] = [
  {
    // Each reader's own interface font — San Francisco on Apple devices, Segoe UI on Windows.
    // Not a brand choice; it is here so a template that never picked a face keeps rendering exactly
    // as every template did before master fonts existed, and so that state can be named.
    id: 'system-sans',
    label: 'System sans',
    stack: "-apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    docsFamily: 'Arial',
    targets: ['email'],
  },
  {
    id: 'arial',
    label: 'Arial',
    stack: 'Arial, Helvetica, sans-serif',
    docsFamily: 'Arial',
    targets: ['email', 'doc'],
  },
  {
    id: 'helvetica',
    label: 'Helvetica',
    stack: 'Helvetica, Arial, sans-serif',
    docsFamily: 'Helvetica',
    targets: ['email', 'doc'],
  },
  {
    id: 'verdana',
    label: 'Verdana',
    stack: 'Verdana, Geneva, sans-serif',
    docsFamily: 'Verdana',
    targets: ['email', 'doc'],
  },
  {
    id: 'tahoma',
    label: 'Tahoma',
    stack: 'Tahoma, Verdana, sans-serif',
    docsFamily: null,
    targets: ['email'],
  },
  {
    id: 'trebuchet',
    label: 'Trebuchet MS',
    stack: "'Trebuchet MS', Tahoma, sans-serif",
    docsFamily: 'Trebuchet MS',
    targets: ['email', 'doc'],
  },
  {
    id: 'calibri',
    label: 'Calibri',
    stack: 'Calibri, Arial, sans-serif',
    docsFamily: null,
    targets: ['email'],
  },
  {
    id: 'aptos',
    label: 'Aptos',
    stack: 'Aptos, Calibri, Arial, sans-serif',
    docsFamily: null,
    targets: ['email'],
  },
  {
    id: 'segoe',
    label: 'Segoe UI',
    stack: "'Segoe UI', Tahoma, sans-serif",
    docsFamily: null,
    targets: ['email'],
  },
  {
    id: 'georgia',
    label: 'Georgia',
    stack: 'Georgia, Times New Roman, serif',
    docsFamily: 'Georgia',
    targets: ['email', 'doc'],
  },
  {
    id: 'times',
    label: 'Times New Roman',
    stack: "'Times New Roman', Times, serif",
    docsFamily: 'Times New Roman',
    targets: ['email', 'doc'],
  },
  {
    id: 'cambria',
    label: 'Cambria',
    stack: 'Cambria, Georgia, serif',
    docsFamily: null,
    targets: ['email'],
  },
  {
    id: 'garamond',
    label: 'Garamond',
    stack: 'Garamond, Georgia, serif',
    docsFamily: 'Garamond',
    targets: ['email', 'doc'],
  },
  {
    id: 'courier',
    label: 'Courier New',
    stack: "'Courier New', Courier, monospace",
    docsFamily: 'Courier New',
    targets: ['email', 'doc'],
  },

  // Google faces. Doc-only on purpose: an email naming one of these reaches almost nobody with it
  // installed, so offering it for email would be offering a font that silently does not apply.
  {
    id: 'dm-sans',
    label: 'DM Sans',
    stack: "'DM Sans', Arial, sans-serif",
    docsFamily: 'DM Sans',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'roboto',
    label: 'Roboto',
    stack: 'Roboto, Arial, sans-serif',
    docsFamily: 'Roboto',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'open-sans',
    label: 'Open Sans',
    stack: "'Open Sans', Arial, sans-serif",
    docsFamily: 'Open Sans',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'lato',
    label: 'Lato',
    stack: 'Lato, Arial, sans-serif',
    docsFamily: 'Lato',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'montserrat',
    label: 'Montserrat',
    stack: 'Montserrat, Arial, sans-serif',
    docsFamily: 'Montserrat',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'inter',
    label: 'Inter',
    stack: 'Inter, Arial, sans-serif',
    docsFamily: 'Inter',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'poppins',
    label: 'Poppins',
    stack: 'Poppins, Arial, sans-serif',
    docsFamily: 'Poppins',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'work-sans',
    label: 'Work Sans',
    stack: "'Work Sans', Arial, sans-serif",
    docsFamily: 'Work Sans',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'nunito',
    label: 'Nunito',
    stack: 'Nunito, Arial, sans-serif',
    docsFamily: 'Nunito',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'raleway',
    label: 'Raleway',
    stack: 'Raleway, Arial, sans-serif',
    docsFamily: 'Raleway',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'ibm-plex-sans',
    label: 'IBM Plex Sans',
    stack: "'IBM Plex Sans', Arial, sans-serif",
    docsFamily: 'IBM Plex Sans',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'merriweather',
    label: 'Merriweather',
    stack: 'Merriweather, Georgia, serif',
    docsFamily: 'Merriweather',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'playfair',
    label: 'Playfair Display',
    stack: "'Playfair Display', Georgia, serif",
    docsFamily: 'Playfair Display',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'libre-baskerville',
    label: 'Libre Baskerville',
    stack: "'Libre Baskerville', Georgia, serif",
    docsFamily: 'Libre Baskerville',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'eb-garamond',
    label: 'EB Garamond',
    stack: "'EB Garamond', Garamond, serif",
    docsFamily: 'EB Garamond',
    targets: ['doc'],
    webFont: true,
  },
  {
    id: 'space-grotesk',
    label: 'Space Grotesk',
    stack: "'Space Grotesk', Arial, sans-serif",
    docsFamily: 'Space Grotesk',
    targets: ['doc'],
    webFont: true,
  },
]

/**
 * Widened to `string` rather than a union of the ids above, on purpose — same reason
 * `fontFamilySchema` is not an enum: a union of 28 literals multiplies through the API's inferred
 * types. A face id is looked up in the catalogue, never switched on. Inferred from the schema
 * below, which is a refined `string`, so the alias tracks it rather than restating it.
 */
export type TemplateFontFamily = z.infer<typeof fontFamilySchema>

const FONT_IDS = new Set<string>(FONT_FACES.map((face) => face.id))

/**
 * A string checked against the catalogue, deliberately NOT a `z.enum`.
 *
 * An enum of 28 members is 28 literal types, and this schema sits inside the template document,
 * which sits inside the API's inferred RPC type — widening the list from five faces to twenty-eight
 * made that type too large for TypeScript to serialise (TS7056) and broke the typed client for the
 * whole app. The membership check is identical at runtime; what changes is that the compiler
 * carries one `string` instead of a union that multiplies through every route.
 *
 * Which is the general lesson for growing this list: faces are data, and data belongs in the
 * catalogue rather than in the type system.
 */
export const fontFamilySchema = z
  .string()
  .refine((id) => FONT_IDS.has(id), { message: 'Unknown typeface' })

export function fontFace(id: string): FontFace | undefined {
  return FONT_FACES.find((face) => face.id === id)
}

/** The faces an editor should offer for a template with this target. */
export function fontsForTarget(target: 'email' | 'doc'): readonly FontFace[] {
  return FONT_FACES.filter((face) => (face.targets as readonly string[]).includes(target))
}

/** The CSS stack for an email. Unknown ids fall through to the renderer's own default. */
export function fontStack(id: string): string | undefined {
  return fontFace(id)?.stack
}

/**
 * The family a Google Doc should be told. Null for a face Google cannot supply — the caller sends
 * nothing rather than a name that would be silently substituted.
 */
export function docsFamily(id: string): string | null {
  return fontFace(id)?.docsFamily ?? null
}

/** Kept as a record because the email renderer indexes it directly. */
export const FONT_STACKS = Object.fromEntries(
  FONT_FACES.map((face) => [face.id, face.stack]),
) as Record<TemplateFontFamily, string>
